import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { AgentTypeSelect } from './ChatTypeSelect';
import { PromptAgentType } from './types';
import { UserCreatedPrompt } from './types';

interface BookmarkPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prompt: Omit<UserCreatedPrompt, 'id' | 'createdAt'>) => void;
  initialDescription?: string;
  initialTitle?: string;
  initialAgentType?: PromptAgentType;
  isEditMode?: boolean;
}

export const BookmarkPromptModal: React.FC<BookmarkPromptModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialDescription = '',
  initialTitle = '',
  initialAgentType,
  isEditMode = false,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const [agentType, setAgentType] = useState<PromptAgentType | null>(null);
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState(initialDescription);

  // Reset form when modal opens with new initial values
  React.useEffect(() => {
    if (isOpen) {
      setPromptDescription(initialDescription);
      setPromptName(initialTitle);
      setAgentType(initialAgentType || null);
    }
  }, [isOpen, initialDescription, initialTitle, initialAgentType]);

  const handleSave = () => {
    if (!agentType || !promptName.trim() || !promptDescription.trim()) return;

    onSave({
      title: promptName.trim(),
      prompt: promptDescription.trim(),
      agentType,
    });

    onClose();
  };

  const MAX_PROMPT_LENGTH = 96000;
  const MAX_NAME_LENGTH = 200;
  const promptLengthExceeded = promptDescription.length > MAX_PROMPT_LENGTH;
  const nameLengthExceeded = promptName.length > MAX_NAME_LENGTH;
  const isFormValid = agentType && promptName.trim() && promptDescription.trim() && !promptLengthExceeded && !nameLengthExceeded;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 300,
          }}
        />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl shadow-2xl p-6 w-[90vw] max-w-[480px]"
          style={{
            zIndex: 301,
            backgroundColor: isDark ? 'var(--dark-main-background, #19191c)' : 'var(--color-bg, #F8F8FF)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            fontFamily: '3MCircularTT, sans-serif',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title
              className="text-lg font-bold"
              style={{ color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)' }}
            >
              {isEditMode ? 'Edit Prompt' : 'Bookmark Prompts'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-md transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                title="Close"
              >
                <Cross2Icon
                  width={16}
                  height={16}
                  style={{ color: isDark ? '#a1a1aa' : '#6b7280' }}
                />
              </button>
            </Dialog.Close>
          </div>

          {/* Chat Type */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)' }}
            >
              Chat Type
            </label>
            <AgentTypeSelect
              value={agentType}
              onChange={setAgentType}
              selectMode="single"
              placeholder="Select Chat Type"
              fullWidth
            />
          </div>

          {/* Prompt Name */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)' }}
            >
              Prompt Name
            </label>
            <input
              type="text"
              value={promptName}
              onChange={(e) => setPromptName(e.target.value)}
              placeholder="Type your comment here..."
              className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              style={{
                backgroundColor: isDark ? 'var(--color-secondary, #313136)' : '#f3f4f6',
                border: `1px solid ${isDark ? (nameLengthExceeded ? 'rgba(239, 68, 68, 0.7)' : 'rgba(255, 255, 255, 0.1)') : (nameLengthExceeded ? 'rgba(239, 68, 68, 0.7)' : 'rgba(0, 0, 0, 0.12)')}`,
                color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)',
                fontFamily: '3MCircularTT, sans-serif',
              }}
            />
            <div className="flex items-center justify-between mt-1">
              {nameLengthExceeded ? (
                <span className="text-xs" style={{ color: '#ef4444' }}>
                  Prompt name exceeds maximum of {MAX_NAME_LENGTH} characters
                </span>
              ) : (
                <span />
              )}
              <span
                className="text-xs"
                style={{ color: nameLengthExceeded ? '#ef4444' : (isDark ? '#71717a' : '#9ca3af') }}
              >
                {promptName.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
          </div>

          {/* Prompt Description */}
          <div className="mb-6">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)' }}
            >
              Prompt Description
            </label>
            <textarea
              value={promptDescription}
              onChange={(e) => setPromptDescription(e.target.value)}
              placeholder="Describe your prompt..."
              rows={4}
              className="w-full rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 scrollbar-dark"
              style={{
                backgroundColor: isDark ? 'var(--color-secondary, #313136)' : '#f3f4f6',
                border: `1px solid ${isDark ? (promptLengthExceeded ? 'rgba(239, 68, 68, 0.7)' : 'rgba(255, 255, 255, 0.1)') : (promptLengthExceeded ? 'rgba(239, 68, 68, 0.7)' : 'rgba(0, 0, 0, 0.12)')}`,
                color: isDark ? 'var(--dark-color-text, #e5e5e5)' : 'var(--color-text, #111827)',
                fontFamily: '3MCircularTT, sans-serif',
              }}
            />
            <div className="flex items-center justify-between mt-1">
              {promptLengthExceeded ? (
                <span className="text-xs" style={{ color: '#ef4444' }}>
                  Prompt exceeds maximum of {MAX_PROMPT_LENGTH} characters
                </span>
              ) : (
                <span />
              )}
              <span
                className="text-xs"
                style={{ color: promptLengthExceeded ? '#ef4444' : (isDark ? '#71717a' : '#9ca3af') }}
              >
                {promptDescription.length}/{MAX_PROMPT_LENGTH}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="text-sm font-medium px-4 py-2 rounded-md transition-colors"
              style={{
                color: isDark ? 'var(--dark-color-text-secondary, #B2B2BD)' : '#6b7280',
                fontFamily: '3MCircularTT, sans-serif',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid}
              className="text-sm font-semibold px-6 py-2 rounded-md transition-colors"
              style={{
                backgroundColor: isFormValid ? '#5E95ED' : isDark ? '#3f3f46' : '#d1d5db',
                color: isFormValid ? '#ffffff' : isDark ? '#71717a' : '#9ca3af',
                cursor: isFormValid ? 'pointer' : 'not-allowed',
                fontFamily: '3MCircularTT, sans-serif',
              }}
            >
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
